import { UserPlus } from 'lucide-react'
import type { UnassignedMember } from '../../redux/types/charterTypes'

const ALL_SECTIONS_COUNT = 11

const STATUS_BADGE: Record<string, { label: string; cls: string }> = {
  not_started: { label: 'Not Started', cls: 'bg-gray-100 text-gray-600' },
  in_progress: { label: 'In Progress', cls: 'bg-blue-100 text-blue-700' },
  completed: { label: 'Completed', cls: 'bg-emerald-100 text-emerald-700' },
}

interface UnassignedRosterPanelProps {
  members: UnassignedMember[]
}

/**
 * Students who have joined the class but have no team yet.
 *
 * A list rather than cards: this is the view a professor scans to decide when
 * to form teams, and a class is 30-60 people rather than the 4 a game room
 * holds. Deliberately read-only — assigning teams stays where it already is.
 */
export function UnassignedRosterPanel({ members }: UnassignedRosterPanelProps) {
  if (members.length === 0) return null

  const readyCount = members.filter(
    (m) => m.prep?.status === 'completed' && m.prep?.profile_approved
  ).length
  const startedCount = members.filter((m) => m.prep != null).length

  return (
    <div>
      <div className='flex items-center gap-2'>
        <UserPlus className='h-5 w-5 text-indigo-500' />
        <h5 className='text-xl font-bold text-dark-blue'>Waiting for a team</h5>
      </div>
      <p className='text-base text-dark-blue mb-4'>
        {members.length === 1
          ? '1 student has joined and has not'
          : `${members.length} students have joined and have not`}{' '}
        been placed on a team. {startedCount} started their prep,{' '}
        {readyCount} ready.
      </p>

      <div className='overflow-hidden rounded-lg border border-gray-200'>
        <table className='w-full text-left text-sm'>
          <thead className='bg-gray-50 text-gray-600'>
            <tr>
              <th className='px-4 py-2 font-medium'>Student</th>
              <th className='px-4 py-2 font-medium'>Prep</th>
              <th className='px-4 py-2 font-medium'>Sections</th>
              <th className='px-4 py-2 font-medium'>Approved</th>
            </tr>
          </thead>
          <tbody className='divide-y divide-gray-100'>
            {members.map((member) => {
              const prep = member.prep
              const completed = prep
                ? Object.keys(prep.completed_sections ?? {}).length
                : 0
              const badge =
                STATUS_BADGE[prep?.status ?? 'not_started'] ??
                STATUS_BADGE.not_started
              return (
                <tr key={member.member_id} className='hover:bg-gray-50'>
                  <td className='px-4 py-2'>
                    <div className='text-dark-blue'>
                      {member.member_name || member.member_email}
                    </div>
                    {member.member_name && (
                      <div className='text-xs text-gray-500'>
                        {member.member_email}
                      </div>
                    )}
                  </td>
                  <td className='px-4 py-2'>
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs ${badge.cls}`}
                    >
                      {badge.label}
                    </span>
                  </td>
                  <td className='px-4 py-2 text-dark-blue'>
                    {completed}/{ALL_SECTIONS_COUNT}
                  </td>
                  <td className='px-4 py-2 text-dark-blue'>
                    {prep?.profile_approved ? 'Yes' : '—'}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
